import React, { useState, useEffect, memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateDevicesPageBulkCreateListModal,
  updateFilteredDevicesWithList,
  updateDevicesPageSelectedDeviceGroupName,
  clearSelectedDevicesList,
  setMapSelectMode,
  setLocalNameFilter,
  addToastNotification,
  doDeviceGroupCreate,
  addFilter,
  clearSelectedDevicesData
} from 'redux/actions';
import { v4 } from 'uuid';
import { Status, SPECIAL_CHARS_FORMAT, LOADING_CIRCLE, DEVICES_PAGE_TAB } from 'common/utils/constants';
import { groupNameInputValidator, trimAndReplaceSpaces, mixedDeviceIdArrayToDeviceIdArray } from 'common/utils/helpers';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import CommissionErrorBox from '../Commissioning/CommissionErrorBox/CommissionErrorBox';
import './CreateDevicesListModal.css';
import { selectStoredBundleDictionary, createStatusSelector, selectDeviceGroups } from 'redux/selectors';

const CreateDevicesListForm = (props) => {
  const { localSelectedDevicesDictionary, handleSave } = props;
  const [localGroupName, setLocalGroupName] = useState('');
  const selectStatus = useMemo(() => createStatusSelector(`doDeviceGroupCreate-${localGroupName}`), [localGroupName]);
  // const [actionPending, setActionPending] = useState(false);
  // const [localSharingEmail, setLocalSharingEmail] = useState('');
  const [localError, setLocalError] = useState(null);
  const [createDeviceGroupErrorCode, setCreateDeviceGroupErrorCode] = useState(null);
  const deviceGroups = useSelector(selectDeviceGroups);
  const bundles = useSelector(selectStoredBundleDictionary);

  const dispatch = useDispatch();
  let defaultButton = <span>Save</span>;
  const [submitButton, setSubmitButton] = useState(defaultButton);
  const [isSaveButtonClicked, setIsSavedButtonClicked] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  let errorMessage = null;
  let noDevicesSelected =
    localSelectedDevicesDictionary &&
    Object.keys(localSelectedDevicesDictionary).every(
      (deviceId) =>
        !localSelectedDevicesDictionary[deviceId].checked && !localSelectedDevicesDictionary[deviceId].matched
    );
  let createDeviceGroupStatus = useSelector(selectStatus);

  useEffect(() => {
    if (createDeviceGroupStatus === Status.PENDING) {
      setSubmitButton(LOADING_CIRCLE);
    } else {
      setSubmitButton(defaultButton);
    }
  }, [createDeviceGroupStatus]);

  const handleOnChange = (e) => {
    if (isSaveButtonClicked) setIsSavedButtonClicked(false);
    if (localError) setLocalError(null);

    let inputValue = e.target.value;
    let inputName = e.target.name;
    if (inputName === 'localGroupName') {
      setLocalGroupName(inputValue);
    }
    // if (inputName === 'localSharingEmail') {
    //   setLocalSharingEmail(inputValue);
    // }
  };

  const handleCreateList = () => {
    setIsSavedButtonClicked(true);
    const selectedDevicesIds = [];
    localSelectedDevicesDictionary &&
      Object.keys(localSelectedDevicesDictionary).forEach((deviceId) => {
        let selectedDeviceObj = localSelectedDevicesDictionary[deviceId];
        if (selectedDeviceObj.checked && selectedDeviceObj.matched) selectedDevicesIds.push(deviceId);
      });

    const existingGroupNames = Object.keys(deviceGroups);
    const trimmedLocalGroupName = trimAndReplaceSpaces(localGroupName);
    const flatSelectedDevicesIds = mixedDeviceIdArrayToDeviceIdArray(selectedDevicesIds, props.selectedDevicesData);

    if (
      trimmedLocalGroupName &&
      !existingGroupNames.includes(trimmedLocalGroupName) &&
      selectedDevicesIds.length > 0 &&
      trimmedLocalGroupName.length <= 50 &&
      !SPECIAL_CHARS_FORMAT.test(trimmedLocalGroupName)
    ) {
      const cb_success = () => {
        dispatch(setLocalNameFilter(''));
        let deviceGroupDevices = [];
        selectedDevicesIds.forEach((deviceId) => {
          let bundle = bundles[deviceId];
          if (bundle) {
            for (let member of bundle.members) {
              deviceGroupDevices.push(member.id);
            }
          } else {
            deviceGroupDevices.push(deviceId);
          }
        });
        handleSave();
        dispatch(
          addToastNotification({
            id: v4(),
            title: trimmedLocalGroupName,
            description: 'is created.',
            groupName: trimmedLocalGroupName
          })
        );
        // dispatch(setFilterDictionary({ deviceId: deviceGroupDevices }));
        dispatch(updateDevicesPageSelectedDeviceGroupName(trimmedLocalGroupName));
        dispatch(updateFilteredDevicesWithList(flatSelectedDevicesIds));
        dispatch(clearSelectedDevicesList(props.activeTab));
        dispatch(clearSelectedDevicesData(props.activeTab));
        dispatch(setMapSelectMode({ bulk: false, allSelect: false, shape: 'none' }));
        dispatch(addFilter(DEVICES_PAGE_TAB.pivot, { deviceId: flatSelectedDevicesIds }));
        dispatch(addFilter(DEVICES_PAGE_TAB.echo, { deviceId: flatSelectedDevicesIds }));
        dispatch(addFilter(DEVICES_PAGE_TAB.all, { deviceId: flatSelectedDevicesIds }));
      };

      const cb_failed = (e) => {
        setCreateDeviceGroupErrorCode(e.message ?? 'Unknown');
      };

      dispatch(doDeviceGroupCreate(trimmedLocalGroupName, isDefault, flatSelectedDevicesIds, cb_success, cb_failed));
    } else if (existingGroupNames.includes(trimmedLocalGroupName)) {
      setLocalError('The name already exists. Please pick another name.');
    } else if (selectedDevicesIds.length === 0) {
      setLocalError('Please at least select one device.');
    } else {
      setLocalError(groupNameInputValidator(trimmedLocalGroupName, existingGroupNames, selectedDevicesIds));
    }
  };

  const handleNewDefault = (event) => {
    const newDefault = !isDefault;
    setIsDefault(newDefault);
  };

  if (localError) {
    errorMessage = <span className="BulkCreateDevicesListModal_ConfigPanel_ErrorMessage">{localError}</span>;
  } else {
    errorMessage = null;
  }

  let overlay = null;
  if (createDeviceGroupErrorCode) {
    overlay = (
      <CommissionErrorBox
        title={`Create Device Group Error: ${createDeviceGroupErrorCode}`}
        message={'Something went wrong. Please try again later.'}
        onClose={() => dispatch(updateDevicesPageBulkCreateListModal(false))}
      />
    );
  }

  return (
    <div className="BulkCreateDevicesListModal_ActionForm_Div">
      {overlay}
      <form
        onSubmit={(e) => e.preventDefault()}
        id="bulkCreateDevicesListForm"
        className="BulkCreateDevicesListModal_ConfigPanel"
      >
        <div className="BulkCreateDevicesListModal_ConfigPanel_Header_Title">Create a new list</div>
        <label className="BulkCreateDevicesListModal_ConfigPanel_InputLabel">
          <span className="BulkCreateDevicesListModal_ConfigPanel_Title TransferTitle">Name the list</span>
          <input
            className="BulkCreateDevicesListModal_ConfigPanel_InputValue"
            type="text"
            name="localGroupName"
            placeholder="Enter name"
            value={localGroupName}
            disabled={noDevicesSelected}
            data-tip={noDevicesSelected ? 'Must select at least one device' : 'Enter a list name'}
            onChange={(e) => handleOnChange(e)}
          />
          {errorMessage}
        </label>
        <div className="BulkCreateDevicesListModal_ConfigPanel_Default_Checkbox_Container">
          <Checkbox isChecked={isDefault} onChange={(event) => handleNewDefault(event)} />
          <span className="BulkCreateDevicesListModal_ConfigPanel_Default_Checkbox_Title">Set as default</span>
        </div>
        {/* <label className="BulkCreateDevicesListModal_ConfigPanel_InputLabel">
          <span className="BulkCreateDevicesListModal_ConfigPanel_Title TransferTitle">
            Share the list with (add email address)
          </span>
          <input
            className="BulkCreateDevicesListModal_ConfigPanel_InputValue"
            type="email"
            name="localSharingEmail"
            placeholder="Enter email"
            value={localSharingEmail}
            onChange={(e) => handleOnChange(e)}
          />
        </label> */}

        <div className="BulkCreateDevicesListModal_ConfigPanel_Buttons_Container">
          <div className="BulkCreateDevicesListModal_ActivationScheduleDropModal_ButtonRow">
            <button
              className="BulkCreateDevicesListModal_ConfigPanel_FinishButtons_CancelButton"
              disabled={!localGroupName}
              onClick={() => dispatch(updateDevicesPageBulkCreateListModal(false))}
            >
              Cancel
            </button>
            <button
              className={'BulkCreateDevicesListModal_List_CreateButton Global_RoundedButton'}
              disabled={!localGroupName || isSaveButtonClicked}
              onClick={() => {
                handleCreateList();
              }}
            >
              {submitButton}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default memo(CreateDevicesListForm);
